/**
 * AI system — first-pass implementation (v0M3.8).
 *
 * Runs once per game-day (every AI_CADENCE_TICKS ticks) and acts on
 * every faction with `isAI === true`. Player + neutral factions are
 * skipped. Behaviour is intentionally simple — a "watchable playtest"
 * bot, not a competitive opponent:
 *
 *  1. **Move**: each AI army pathfinds toward the nearest enemy army
 *     (different teamId, non-neutral). If no enemy army exists, the
 *     army idles. Settlement-targeting + capital-priority is a v0M3.8b
 *     candidate (needs Settlement.cell field to land first).
 *  2. **Recruit**: at the first eligible Town/Settlement province (has
 *     Population ≥ Swordsman.populationCost, faction has all needed
 *     resources), queue a Swordsman recruit. Attaches to the first
 *     army with < commanderCohortLimit cohorts, else spawns a new
 *     army at completion (armyId: null in the queue entry).
 *  3. **Build**: at the highest-population owned province (proxy for
 *     capital until Faction.capitalSettlementId lands), queue the next
 *     structure in FIXED order Market → Farm → Blacksmith. Skips if
 *     already built or queued; skips if slot cap reached; skips if
 *     Gold below spec cost.
 *
 * Determinism: iterates factions + armies + provinces via `sortedIds`.
 * RNG unused at v0M3.8 first pass (deterministic decisions). Any
 * future randomised decisions (tie-break between equidistant targets,
 * e.g.) draw from `state.rng` via `nextInt`.
 *
 * Difficulty hooks: `difficulty` is captured in the Scenario but not
 * yet consumed at v0M3.8. When v0M3.8b tunes AI, look for
 * `DIFFICULTY_HOOK:` comments below — those are the tuning insertion
 * points.
 */

import { hierarchicalPath } from './pathfind';
import { computeBridgeCells } from './build';
import { WORLD_BRIDGE_MOVE_MULTIPLIER } from './apply-commands';
import { loadCohorts } from '../data/cohorts';
import { loadStructures } from '../data/structures';
import { loadSpecialUnits } from '../data/special-units';
import { NEUTRAL_TEAM_ID } from './scenario';
import { commanderCohortLimit, sortedIds } from './state';
import type { Spy, SettlementId } from './state';
import { ARMY_CREATION_GOLD_COST, MAX_ARMIES_PER_FACTION, NEW_ARMY_SUPPLY_COST } from './apply-commands';
import { maxArmyBonus } from './knowledge-effects';
import { loadKnowledge } from '../data/knowledge';

// v0M9.9 (2026-07-24) — AI-side effective-cap resolution mirrors the
// apply-commands path so player + AI both benefit from the Command and
// Control chain when researched.
const AI_KNOWLEDGE = loadKnowledge();
import type { Command } from './commands';
import type { RngState } from './rng';
import type { SimContext } from './sim-context';
import type {
  Army,
  Faction,
  GameState,
  Province,
  SettlementTier,
} from './state';
import { TICKS_PER_GAME_DAY } from './state';

/**
 * How often the AI acts. TICKS_PER_GAME_DAY ticks = 1 game-day per
 * MECHANICS.md §1.1 + MFix3.8. Once per game-day is enough resolution
 * for strategic decisions and avoids per-tick pathfind cost.
 */
export const AI_CADENCE_TICKS = TICKS_PER_GAME_DAY;

const AI_STARTING_COHORT_TYPE = 'Swordsman';

/**
 * Fixed structure build order for AI capitals per Michael's v0M3.8 lock.
 *
 * MFix17 (2026-07-29) — prepended the structure prereq of
 * `AI_STARTING_COHORT_TYPE` (currently Swordsman → Barracks) so the AI
 * unblocks its own recruit path. Before this fix, the recruit-prereq
 * gate at aiRecruit() silently rejected every attempt because the AI's
 * fixed order (Market → Farm → Blacksmith) never built the required
 * cohort-enabling structure — so the AI would idle indefinitely once
 * its starting army died, unable to raise a replacement. If the xlsx
 * later changes Swordsman's prereq or the AI's starting cohort, update
 * this list to keep the first entry aligned with the new prereq.
 */
const AI_BUILD_ORDER: readonly string[] = ['Barracks', 'Market', 'Farm', 'Blacksmith'];

/** Structure caps per tier from ECONOMY.md §2.1 — matches apply-commands.ts. */
const STRUCTURE_CAP_BY_TIER: Record<SettlementTier, number> = {
  Village: 5,
  Town: 10,
  City: 20,
};

const COHORTS = loadCohorts();
const STRUCTURES = loadStructures();

export function aiSystem(
  state: GameState,
  _commands: readonly Command[],
  _rng: RngState,
  context: SimContext,
): GameState {
  // Only act on game-day boundaries.
  if (state.tick % AI_CADENCE_TICKS !== 0) return state;

  for (const factionId of sortedIds(state.factions)) {
    const faction = state.factions.get(factionId)!;
    if (!faction.isAI) continue; // skip player + neutrals
    if (faction.teamId === NEUTRAL_TEAM_ID) continue; // defensive

    aiMoveArmies(state, faction, context);
    aiRecruit(state, faction);
    aiBuildAtCapital(state, faction);
    // v0M5.4c (2026-07-25) — spy loop: recruit + move + enter.
    aiSpyBehavior(state, faction, context);
    // v0M7.6 (2026-07-25) — Engineer build behaviour: opportunistic
    // Watch Tower placement at an Engineer-Army's current cell.
    aiEngineerBehavior(state, faction);
  }
  return state;
}

/**
 * v0M7.6 (2026-07-25) — AI Engineer behaviour (MVP).
 *
 * For each Army owned by this faction that:
 *   - carries the `Engineer` attachment,
 *   - has no active buildOrder,
 *   - is not in a battle,
 *   - is on a cell that isn't already occupied by another WorldStructure
 *     or Settlement,
 * AND the faction has researched `Watch Tower Construction`, issue a
 * `Build Watch Tower` order at the Army's current cell. `aiMoveArmies`
 * skips armies with active buildOrder so the Engineer-Army will stay
 * parked and the build system accrues progress each tick until the
 * per-kind buildTimeDays threshold fires and the Watch Tower spawns.
 *
 * Watch Tower only at v1 — Road / Bridge / Fort AI behaviour is
 * deferred. Watch Tower is the cheapest + highest-value early buildable
 * (extends vision immediately) and doesn't require infrastructure
 * decisions (Bridge needs a river cell; Fort is a strategic commitment;
 * Road needs a chain to be useful).
 */
function aiEngineerBehavior(state: GameState, faction: Faction): void {
  const researched = faction.researchedKnowledge ?? [];
  if (!researched.includes('Watch Tower Construction')) return;

  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId);
    if (!army) continue;
    if (army.ownerFactionId !== faction.id) continue;
    if (army.currentBattleId !== null) continue;
    if (army.buildOrder != null) continue;
    const attachments = army.attachments ?? [];
    if (!attachments.includes('Engineer')) continue;

    const cx = Math.floor(army.position.x);
    const cy = Math.floor(army.position.y);
    // Skip if the cell is occupied.
    let occupied = false;
    if (state.worldStructures) {
      for (const ws of state.worldStructures.values()) {
        if (ws.cell.x === cx && ws.cell.y === cy) { occupied = true; break; }
      }
    }
    if (!occupied) {
      for (const settlement of state.settlements.values()) {
        if (settlement.cell.x === cx && settlement.cell.y === cy) { occupied = true; break; }
      }
    }
    if (occupied) continue;

    army.buildOrder = {
      kind: 'watchtower',
      targetCell: { x: cx, y: cy },
      progressDays: 0,
    };
  }
}

// ---------------------------------------------------------------------------
// Move — pathfind each army toward the nearest enemy army
// ---------------------------------------------------------------------------

function aiMoveArmies(state: GameState, faction: Faction, context: SimContext): void {
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId)!;
    if (army.ownerFactionId !== faction.id) continue;
    // Locked in a battle — no strategic movement.
    if (army.currentBattleId !== null) continue;
    // v0M7.6 (2026-07-25) — Engineer-Army with an active build order
    // must stay parked on the target cell. Skipping the move pass
    // means aiMoveArmies won't clobber the parked position with a
    // move-toward-enemy plan mid-build.
    if (army.buildOrder != null) continue;

    const target = findNearestEnemyArmy(state, faction, army);
    if (target === null) continue;

    const startX = Math.round(army.position.x);
    const startY = Math.round(army.position.y);
    const goalX = Math.round(target.position.x);
    const goalY = Math.round(target.position.y);

    // Same-cell / adjacent — no need to repath (ZoC-detection will fire).
    if (startX === goalX && startY === goalY) continue;

    const path = hierarchicalPath(
      context.terrain,
      context.regionGraph,
      startX,
      startY,
      goalX,
      goalY,
      computeBridgeCells(state, context.terrain.width),
      WORLD_BRIDGE_MOVE_MULTIPLIER,
    );
    if (!path.found) continue;

    const smoothed = path.smoothedCells;
    const waypoints = smoothed.length > 1 ? smoothed.slice(1) : smoothed.slice();
    army.plannedPath = waypoints;
    army.movementGoal = { x: goalX, y: goalY };
  }
}

/**
 * Find the closest enemy army (different teamId, non-neutral) to `army`.
 * Ties broken by lower armyId (deterministic).
 */
function findNearestEnemyArmy(state: GameState, faction: Faction, army: Army): Army | null {
  let best: Army | null = null;
  let bestDistSq = Infinity;
  for (const otherId of sortedIds(state.armies)) {
    const other = state.armies.get(otherId)!;
    if (other.id === army.id) continue;
    const otherFaction = state.factions.get(other.ownerFactionId);
    if (!otherFaction) continue;
    if (otherFaction.teamId === faction.teamId) continue; // same team
    if (otherFaction.teamId === NEUTRAL_TEAM_ID) continue; // ignore neutrals
    const dx = other.position.x - army.position.x;
    const dy = other.position.y - army.position.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      best = other;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Recruit — queue a Swordsman at the first eligible province
// ---------------------------------------------------------------------------

function aiRecruit(state: GameState, faction: Faction): void {
  const spec = COHORTS[AI_STARTING_COHORT_TYPE];
  if (!spec) return; // Balance.xlsx author error — degrade silently
  // DIFFICULTY_HOOK: at v0M3.8b, gate this by difficulty (e.g. Hard = recruit at
  // any resource threshold; Easy = require higher surplus).

  for (const provinceId of sortedIds(state.provinces)) {
    const province = state.provinces.get(provinceId)!;
    if (province.ownerFactionId !== faction.id) continue;
    if (province.tier === 'Village') continue; // recruitment only at Town+

    if (!hasResourcesForCohort(faction, spec)) return; // stop entire recruit pass; no province will succeed either
    if (province.population < spec.populationCost) continue;

    // Structure prereq: Swordsman has none in v1 Balance, but be defensive.
    let prereqsMet = true;
    for (const prereq of spec.structurePrereq) {
      if (!province.structures.some((s) => s.name === prereq)) {
        prereqsMet = false;
        break;
      }
    }
    if (!prereqsMet) continue;

    // Find a target army with room; fall back to new army (armyId: null).
    const targetArmyId = findRecruitTargetArmy(state, faction);

    // MFix1.4 (2026-07-16): new-army creation gated on faction army cap +
    // ARMY_CREATION_GOLD_COST surcharge. Playtest fix: AI was spamming
    // 1-cohort armies. If the cap is hit or the surcharge is unaffordable,
    // skip this recruit entirely rather than attaching to an existing
    // army — the AI's existing armies are usually already full, so
    // silently attaching would rarely succeed; skipping surfaces the
    // rejection cleanly for later observation.
    const isNewArmy = targetArmyId === null;
    if (isNewArmy) {
      let ownedArmies = 0;
      for (const a of state.armies.values()) {
        if (a.ownerFactionId === faction.id) ownedArmies++;
      }
      let queuedNewArmies = 0;
      for (const p of state.provinces.values()) {
        if (p.ownerFactionId !== faction.id) continue;
        for (const order of p.recruitQueue) {
          // v0M4.2.1: only NEW-army recruits count against the cap.
          // Garrison recruits carry `settlementId !== null` — skip them here.
          if (order.armyId === null && order.settlementId === null) queuedNewArmies++;
        }
      }
      const effectiveArmyCap = MAX_ARMIES_PER_FACTION + maxArmyBonus(faction, AI_KNOWLEDGE);
      if (ownedArmies + queuedNewArmies >= effectiveArmyCap) return;
      if (faction.resources.gold < spec.goldCost + ARMY_CREATION_GOLD_COST) return;
      // v0M10.4 (2026-07-24) — new armies also cost supplies. Mirror
      // the apply-commands gate so the AI doesn't dry-run into a spawn
      // that would fail resource check.
      if (faction.resources.supplies < spec.suppliesCost + NEW_ARMY_SUPPLY_COST) return;
    }
    const extraGold = isNewArmy ? ARMY_CREATION_GOLD_COST : 0;
    const extraSupplies = isNewArmy ? NEW_ARMY_SUPPLY_COST : 0;

    // Commit: deduct + append. Mirrors apply-commands.ts commit block.
    faction.resources.gold -= spec.goldCost + extraGold;
    faction.resources.iron -= spec.ironCost;
    faction.resources.wood -= spec.woodCost;
    faction.resources.horses -= spec.horsesCost;
    faction.resources.supplies -= spec.suppliesCost + extraSupplies;
    province.population -= spec.populationCost;
    province.recruitQueue.push({
      cohortType: AI_STARTING_COHORT_TYPE,
      armyId: targetArmyId,
      settlementId: null,
      progressSec: 0,
    });
    return; // one recruit per faction per day for v0M3.8 first pass
  }
}

function hasResourcesForCohort(faction: Faction, spec: (typeof COHORTS)[string]): boolean {
  return (
    faction.resources.gold >= spec.goldCost &&
    faction.resources.iron >= spec.ironCost &&
    faction.resources.wood >= spec.woodCost &&
    faction.resources.horses >= spec.horsesCost &&
    faction.resources.supplies >= spec.suppliesCost
  );
}

/**
 * Pick the first (by armyId) army of this faction with room under its
 * Commander's cohort limit, counting already-queued recruits toward it.
 * Returns `null` to spawn a new army at recruit completion.
 */
function findRecruitTargetArmy(state: GameState, faction: Faction): Army['id'] | null {
  for (const armyId of sortedIds(state.armies)) {
    const army = state.armies.get(armyId)!;
    if (army.ownerFactionId !== faction.id) continue;
    const commander = state.commanders.get(army.commanderId);
    if (!commander) continue;
    const limit = commanderCohortLimit(commander.level);
    let inQueue = 0;
    for (const provinceId of state.provinces.keys()) {
      const p = state.provinces.get(provinceId)!;
      for (const order of p.recruitQueue) {
        if (order.armyId === army.id) inQueue++;
      }
    }
    if (army.cohortIds.length + inQueue < limit) return army.id;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Build — queue next structure at highest-population (proxy-capital) province
// ---------------------------------------------------------------------------

function aiBuildAtCapital(state: GameState, faction: Faction): void {
  const capital = findCapitalProvince(state, faction);
  if (!capital) return;

  // Which structure comes next in the fixed order?
  const nextStructure = pickNextBuildTarget(capital);
  if (!nextStructure) return; // all in order already built or queued

  const spec = STRUCTURES[nextStructure];
  if (!spec) return; // Balance.xlsx author error

  // Cap check.
  const cap = STRUCTURE_CAP_BY_TIER[capital.tier];
  if (capital.structures.length + capital.buildQueue.length >= cap) return;

  // Gold check.
  if (faction.resources.gold < spec.goldCost) return;

  // Commit.
  faction.resources.gold -= spec.goldCost;
  capital.buildQueue.push({ name: nextStructure, progressSec: 0 });
}

/**
 * "Capital" = highest-population owned province. Deterministic (ties
 * broken by lower id via sortedIds order-of-first-max). Returns null if
 * the faction owns no provinces.
 */
function findCapitalProvince(state: GameState, faction: Faction): Province | null {
  let best: Province | null = null;
  for (const provinceId of sortedIds(state.provinces)) {
    const province = state.provinces.get(provinceId)!;
    if (province.ownerFactionId !== faction.id) continue;
    if (best === null || province.population > best.population) {
      best = province;
    }
  }
  return best;
}

/**
 * Return the next AI_BUILD_ORDER entry that isn't already built or in the
 * queue at this province. Returns null when the entire order is
 * satisfied.
 */
function pickNextBuildTarget(province: Province): string | null {
  for (const name of AI_BUILD_ORDER) {
    const alreadyBuilt = province.structures.some((s) => s.name === name);
    const alreadyQueued = province.buildQueue.some((q) => q.name === name);
    if (!alreadyBuilt && !alreadyQueued) return name;
  }
  return null;
}

/** Convenience helper — factions the AI treats as targets. Exported for testing. */
export function isEnemyOf(a: Faction, b: Faction): boolean {
  if (a.id === b.id) return false;
  if (a.teamId === b.teamId) return false;
  if (a.teamId === NEUTRAL_TEAM_ID) return false;
  if (b.teamId === NEUTRAL_TEAM_ID) return false;
  return true;
}

// ---------------------------------------------------------------------------
// v0M5.4c (2026-07-25) — AI spy behaviour
// ---------------------------------------------------------------------------

const AI_SPECIAL_UNITS = loadSpecialUnits();
const AI_SPY_TYPE = 'Spy';

/**
 * v0M5.4c — Basic AI spy loop, runs once per game-day per AI faction:
 *
 *   1. If the faction has no active spy AND has the Spy's structure
 *      prereq (Thieves Guild) somewhere AND can afford `goldCost`,
 *      spawn a Spy at the capital cell. Mirrors the recruit_spy
 *      handler's immediate-spawn behaviour (MVP; no queue at v0M5.3+).
 *   2. For each own spy currently on the map (not inside a settlement):
 *      target the nearest enemy player capital, path there.
 *   3. If the spy is within Chebyshev 1 of an enemy settlement, enter
 *      it (set `insideSettlementId` + reset `infiltrationDays`; CE
 *      defaults false because target is enemy-owned).
 *
 * Design premise: AI produces exactly one spy per faction, then that
 * spy dies (Counter-Espionage) or stays parked inside an enemy
 * settlement forever. Enough to make CE playtestable; deeper AI spy
 * strategy is a post-v1 pass.
 */
function aiSpyBehavior(state: GameState, faction: Faction, context: SimContext): void {
  const spec = AI_SPECIAL_UNITS[AI_SPY_TYPE];
  if (!spec) return;

  // Step 1 — recruit if none exist.
  const ownSpies: Spy[] = [];
  if (state.spies) {
    for (const spy of state.spies.values()) {
      if (spy.ownerFactionId === faction.id) ownSpies.push(spy);
    }
  }
  if (ownSpies.length === 0) aiTryRecruitSpy(state, faction, spec);

  // Step 2/3 — move + enter each own on-map spy.
  for (const spy of ownSpies) {
    if (spy.insideSettlementId != null) continue; // Already inside; stay put.
    const target = findNearestEnemyCapital(state, faction);
    if (!target) continue;
    // Enter if adjacent.
    const dx = Math.abs(spy.position.x - (target.cell.x + 0.5));
    const dy = Math.abs(spy.position.y - (target.cell.y + 0.5));
    if (Math.max(dx, dy) <= 1.5) {
      spy.plannedPath = null;
      spy.movementGoal = null;
      spy.insideSettlementId = target.id;
      spy.infiltrationDays = 0;
      // Target is enemy-owned by construction — CE stays false.
      spy.counterEspionage = false;
      continue;
    }
    // Otherwise, path toward the target.
    const startX = Math.round(spy.position.x);
    const startY = Math.round(spy.position.y);
    const goalX = Math.round(target.cell.x);
    const goalY = Math.round(target.cell.y);
    if (startX === goalX && startY === goalY) continue;
    const path = hierarchicalPath(context.terrain, context.regionGraph, startX, startY, goalX, goalY, computeBridgeCells(state, context.terrain.width), WORLD_BRIDGE_MOVE_MULTIPLIER);
    if (!path.found) continue;
    const smoothed = path.smoothedCells;
    const waypoints = smoothed.length > 1 ? smoothed.slice(1) : smoothed.slice();
    spy.plannedPath = waypoints;
    spy.movementGoal = { x: goalX, y: goalY };
  }
}

function aiTryRecruitSpy(
  state: GameState,
  faction: Faction,
  spec: import('../data/special-units').SpecialUnitSpec,
): void {
  if (faction.resources.gold < spec.goldCost) return;
  // Find any owned province with the structure_prereq built (Thieves
  // Guild for Spy at v1) — and enqueue at the first eligible
  // province's settlement. v0M5.4d (2026-07-25): enqueue path,
  // matching the player recruit_spy handler in apply-commands.ts.
  // Also skip if an existing Spy order is already in-flight in any of
  // this faction's queues — otherwise the AI would burn gold on a
  // second recruit before the first completes.
  let target: { province: import('./state').Province; settlementId: SettlementId } | null = null;
  for (const provinceId of sortedIds(state.provinces)) {
    const province = state.provinces.get(provinceId);
    if (!province || province.ownerFactionId !== faction.id) continue;
    // Skip if this province already has a Spy queued.
    if (province.recruitQueue.some((o) => o.specialUnitType === AI_SPY_TYPE)) return;
    if (spec.structurePrereq !== '' && !province.structures.some((s) => s.name === spec.structurePrereq)) continue;
    if (target) continue; // Already found a target; keep scanning for the skip check.
    for (const settlementId of sortedIds(state.settlements)) {
      const settlement = state.settlements.get(settlementId);
      if (!settlement) continue;
      if (settlement.provinceId !== province.id) continue;
      target = { province, settlementId: settlement.id };
      break;
    }
  }
  if (!target) return;
  // Learning prereq (blank at v1) — skip.
  faction.resources.gold -= spec.goldCost;
  target.province.recruitQueue.push({
    cohortType: '',
    armyId: null,
    settlementId: target.settlementId,
    specialUnitType: AI_SPY_TYPE,
    progressSec: 0,
  });
}

/**
 * Find the nearest enemy player-faction capital settlement (i.e. the
 * settlement id stored on any enemy faction's `capitalSettlementId`).
 * Ignores neutrals + own team. Ties broken by lowest settlement id
 * for determinism.
 */
function findNearestEnemyCapital(
  state: GameState,
  faction: Faction,
): { id: SettlementId; cell: { x: number; y: number } } | null {
  const enemyCapitalIds: SettlementId[] = [];
  for (const otherId of sortedIds(state.factions)) {
    const other = state.factions.get(otherId);
    if (!other || other.id === faction.id) continue;
    if (other.teamId === faction.teamId) continue;
    if (other.teamId === NEUTRAL_TEAM_ID) continue;
    if (other.capitalSettlementId !== undefined) {
      enemyCapitalIds.push(other.capitalSettlementId);
    }
  }
  if (enemyCapitalIds.length === 0) return null;
  // Pick the first (lowest-id) enemy capital for determinism at v0M5.4c;
  // a smarter AI would pick nearest-Euclidean or highest-value.
  enemyCapitalIds.sort((a, b) => (a as unknown as number) - (b as unknown as number));
  const targetId = enemyCapitalIds[0]!;
  const target = state.settlements.get(targetId);
  if (!target) return null;
  return { id: target.id, cell: target.cell };
}
